import { defineStore } from "pinia";
import { userApi, signApi } from "@/api";
import type {
  UserInfo,
  ApiResponse,
  UserListParams,
  PaginationInfo,
} from "@/api";
import { getLoginUrlApi, getUrlParamsObj } from "@/utils";
import { PLATFORM, GAME_ID } from "./index";
import { clickButton } from "@/utils/point";
export enum NoticeTab {
  Notification = "notify",
  Reply = "reply",
  Mention = "at",
  Like = "like",
}
// 官方账号 等后端支持后会移除
export const OFFICIAL_ACCOUNT_LIST = [
  10000001, 10000002, 10000003, 10000004, 10000005, 10000006, 10000007,
  10000008, 10000009, 10000010, 10000011, 10000012, 10053512, 10053514, 10124208, 10124210,
  10124211, 10124586, 10124927,
];
export const useUserStore = defineStore("user", {
  state: () => ({
    // 用户信息
    userInfo: null as UserInfo | null,
    // uid
    uid: 0,
    // 登录状态
    isLogin: false,
    // 加载状态
    loading: false,
    // 错误信息
    error: null as string | null,
    // 是否已绑定角色
    isBound: false,
    // 用户列表
    userList: [] as UserInfo[],
    // 用户列表加载状态
    userListLoading: true,
    // 分页信息
    pagination: null as PaginationInfo | null,
    // 是否正在加载更多
    loadingMore: false,
    // 关注列表
    followList: [] as UserInfo[],
    // 粉丝列表
    fansList: [] as UserInfo[],
    // 关注数
    followCount: 0,
    // 粉丝数
    fansCount: 0,
    // 点赞数
    likesCount: 0,
    // 金币数
    coins: 0,
    // 头像索引
    avatarIndex: 0,
    // 头像框
    avatarFrame: null as string | null,
    // 身份组信息
    userTitle: null as string[] | null,
    // 是否已经签到
    isSigned: false,
    // 绑定信息
    bindInfo: null as BindInfo | null,
    // 通知相关状态
    mentionUnreadCount: 0,
    postUnreadCount: 0,
    notifyCount: 0,
    likeUnreadCount: 0,
    totalUnreadCount: 0,
    // 通知详情
    notificationDetail: [] as any[],
    // 通知分页器
    notificationPagination: null as PaginationInfo | null,
    // 是否有新增粉丝
    newFollowerCount: 0,
    // 当前选中的通知标签页
    curNoticeTab: NoticeTab.Notification,
    // 通知加载状态
    notificationLoading: false,
    // 通知加载更多状态
    notificationLoadingMore: false,
    // 真实姓名修改记录
    realNameRecord: [] as any[],
    // 绑定角色弹窗显隐状态
    showBindModal: false,
    // 是否官方账号
    isOfficialAccount: false,
  }),

  getters: {
    // 获取用户名
    username: (state) => state.userInfo?.username,
  },

  actions: {
    /**
     * 用户登录
     * @param token 登录token
     */
    async login(token: string) {
      this.loading = true;
      this.error = null;
      // 获取 URL 中的所有参数
      const urlParams = getUrlParamsObj();
      // 合并 token 和 GAME_ID 以及 URL 参数
      const params = {
        token,
        game_id: GAME_ID,
        ...urlParams,
      };
      const response = await userApi.login(params);
      if (response.code == 200) {
        this.isLogin = true;
      } else {
        this.error = response.message || "登录失败";
      }
      this.loading = false;

      return response;
    },

    /**
     * 用户登出
     */
    async logout() {
      this.loading = true;
      this.error = null;

      try {
        const response = await userApi.logout();
        // 无论服务器响应如何，都清除本地状态
        this.clearUserInfo();
        return response;
      } catch (error) {
        this.error = error instanceof Error ? error.message : "登出失败";
        // 即使登出失败，也清除本地状态
        this.clearUserInfo();
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 获取用户信息
     */
    async getUserInfo() {
      this.loading = true;
      this.error = null;

      try {
        const response = await userApi.getInfo(GAME_ID);
        if (response.code === 200) {
          // code == 200 表示用户已登录
          this.userInfo = response.data;
          this.uid = response.data.uid;
          this.followCount = response.data.following_count;
          this.fansCount = response.data.followers_count;
          this.likesCount = response.data.likes_count;
          this.avatarIndex = response.data.avatar || 0;
          this.avatarFrame = response.data.avatar_frame || "";
          this.userTitle = response.data.user_title || null;
          this.coins = response.data.total_reward;
          this.isSigned = response.data.has_signed_today;
          this.bindInfo = response.data.bind_info;
          this.isBound = response.data.bind_info?.role_id ? true : false;
          this.isOfficialAccount = response.data.is_official;
          this.isLogin = true;
          this.getNotificationCount(response.data.uid);
          this.getNotificationDetail(NoticeTab.Notification);
          this.getHasNewFollower();
          return response;
        } else {
          // 其他状态码表示未登录或获取失败
          this.userInfo = null;
          this.isLogin = false;
          this.error = response.message || "用户未登录";
          return response;
        }
      } catch (error) {
        this.error =
          error instanceof Error ? error.message : "获取用户信息失败";
        this.userInfo = null;
        this.isLogin = false;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 获取用户列表（支持分页）
     * @param params 分页参数
     * @param append 是否追加到现有列表
     */
    async getUserList(params?: UserListParams, append: boolean = false) {
      if (append) {
        this.loadingMore = true;
      } else {
        this.userListLoading = true;
      }
      const response = await userApi.getList({
        ...params,
        game_id: GAME_ID,
      });
      if (response.code === 200) {
        if (append && response.data) {
          // 追加模式：将新数据添加到现有列表
          this.userList = [...this.userList, ...response.data];
        } else if (response.data) {
          // 非追加模式：替换现有列表
          this.userList = response.data;
        }

        // 更新分页信息
        this.pagination = response.pagination || null;
        if (append) {
          this.loadingMore = false;
        } else {
          this.userListLoading = false;
        }
        return response;
      }
    },

    /**
     * 清除用户列表
     */
    clearUserList() {
      this.userList = [];
      this.pagination = null;
      this.userListLoading = true;
    },

    /**
     * 关注用户
     * @param fuid - 用户ID
     */
    async follow(fuid: number) {
      if (!this.checkLogin()) {
        return;
      }
      const response = await userApi.follow(fuid, GAME_ID);
      clickButton({
        eventType: "关注",
        eventReason: "关注用户",
        eventValue: JSON.stringify({
          fuid,
          user: this.uid,
        }),
      });
      if (response.code == 200) {
        this.getUserInfo();
        return response;
      }
    },

    /**
     * 取消关注用户
     * @param fuid - 用户ID
     */
    async unfollow(fuid: number) {
      if (!this.checkLogin()) {
        return;
      }
      const response = await userApi.unfollow(fuid, GAME_ID);
      clickButton({
        eventType: "取消关注",
        eventReason: "取消关注用户",
        eventValue: JSON.stringify({
          fuid,
          user: this.uid,
        }),
      });
      if (response.code == 200) {
        this.getUserInfo();
        return response;
      }
    },
    /**
     * 签到
     */
    async sign() {
      const response = await signApi.sign(GAME_ID);
      if (response.code == 200) {
        this.getUserInfo();
        return response;
      }
    },

    /**
     * 搜索用户（带分页与追加）
     * @param keyword 搜索关键词
     * @param params  可选分页参数
     * @param append  是否追加
     */
    async searchUser(
      keyword: string,
      params: Partial<UserListParams> = {},
      append = false
    ) {
      if (!append) {
        this.userListLoading = true;
      }
      const response = await userApi.search({
        ...params,
        keyword,
        game_id: GAME_ID,
      });
      if (response.code === 200 && response.data) {
        if (append) {
          this.userList = [...this.userList, ...response.data];
        } else {
          this.userList = response.data;
        }
        this.pagination = response.pagination || null;
        this.userListLoading = false;

        return response.data;
      }
    },

    /**
     * 加载更多用户或搜索更多用户
     * @param pageSize 每页数量
     * @param keyword  搜索关键词，无则获取全量
     */
    async loadMoreUsers(pageSize: number = 20, keyword: string = "") {
      if (!this.pagination?.has_next || this.loadingMore) {
        return;
      }
      this.loadingMore = true;
      const nextPage = (this.pagination?.current_page || 0) + 1;
      try {
        if (keyword) {
          await this.searchUser(
            keyword,
            { page: nextPage, page_size: pageSize },
            true
          );
        } else {
          await this.getUserList(
            { page: nextPage, page_size: pageSize, game_id: GAME_ID },
            true
          );
        }
      } finally {
        this.loadingMore = false;
      }
    },

    /**
     * 获取关注列表
     * @param uid - 用户ID
     */
    async getFollowList(uid: number, type: "following" | "followers") {
      const response = await userApi.getFollowList(uid, type, GAME_ID);
      if (response.code == 200) {
        if (type == "following") {
          this.followList = response.data;
        } else {
          this.fansList = response.data;
        }
      }
    },

    /**
     * 清除用户信息
     */
    clearUserInfo() {
      this.userInfo = null;
      this.uid = 0;
      this.avatarIndex = 0;
      this.avatarFrame = null;
      this.userTitle = null;
      this.isSigned = false;
      this.bindInfo = null;
      this.isLogin = false;
      this.error = null;
      this.isBound = false;
      this.showBindModal = false;
    },

    /**
     * 更新用户真实姓名
     * @param realname - 真实姓名
     */
    async updateRealName(realname: string) {
      const response = await userApi.updateRealName(realname, GAME_ID);
      if (response.code == 200) {
        this.getUserInfo();
      }
      return response;
    },

    /**
     * 更新用户头像
     * @param avatar - 头像
     */
    async updateAvatar(avatar: number) {
      const response = await userApi.updateAvatar(avatar, GAME_ID);
      if (response.code == 200) {
        this.getUserInfo();
      }
    },

    /**
     * 检查登录状态
     * 如果未登录则跳转到登录页面
     */
    checkLogin(autoJump = true): boolean {
      if (this.isLogin) return true;
      // 从URL参数获取游戏ID，如果没有则使用默认值
      const loginUrl = getLoginUrlApi(PLATFORM, GAME_ID);
      if (autoJump) {
        window.location.href = loginUrl;
      }
      return false;
    },

    /**
     * 检查用户是否已绑定角色
     * 如果未绑定，自动打开绑定角色弹窗
     * @param checkOfficialAccount 是否自动打开弹窗，默认为 true
     */
    checkBind(checkOfficialAccount: boolean = true): boolean {
      // 如果用户未登录，先检查登录状态
      if (!this.checkLogin()) {
        return false;
      }
      // 检查用户是否已绑定角色
      if (this.isBound) {
        return true;
      }
      // 是否需要检查官方账号,此时如果为官方账号，则不校验
      if (checkOfficialAccount && this.isOfficialAccount) {
        return true;
      }
      // 未绑定时，自动打开弹窗
      this.showBindModal = true;

      return false;
    },

    /**
     * 打开绑定角色弹窗
     */
    openBindModal() {
      this.showBindModal = true;
    },

    /**
     * 获取通知数量
     * @param uid 用户ID
     */
    async getNotificationCount() {
      const response = await userApi.getInfoNotificationCount(
        this.uid,
        GAME_ID
      );
      if (response.code == 200) {
        const data = response.data;
        this.mentionUnreadCount = data.notification_modules?.at?.unread_count;
        this.postUnreadCount = data.notification_modules?.reply?.unread_count;
        this.notifyCount = data.notification_modules?.notify?.unread_count;
        this.likeUnreadCount = data.notification_modules?.like?.unread_count;
        this.totalUnreadCount = data.total_unread;
      } else {
        throw new Error(response.message);
      }
    },

    /**
     * 获取通知详情
     * @param type 通知类型
     * @param page 页码
     * @param page_size 每页数量
     * @param append 是否追加到现有列表
     */
    async getNotificationDetail(
      type: NoticeTab,
      page: number = 1,
      page_size: number = 10,
      append: boolean = false
    ) {
      if (append) {
        this.notificationLoadingMore = true;
      } else {
        this.notificationLoading = true;
      }

      try {
        const response = await userApi.getInfoNotificationDetail(
          type,
          GAME_ID,
          page,
          page_size
        );
        if (response.code == 200) {
          if (append && response.data) {
            // 追加模式：将新数据添加到现有列表
            this.notificationDetail = [
              ...this.notificationDetail,
              ...response.data,
            ];
          } else if (response.data) {
            // 非追加模式：替换现有列表
            this.notificationDetail = response.data;
          }
          this.notificationPagination = response.pagination;
        } else {
          throw new Error(response.message);
        }
      } finally {
        if (append) {
          this.notificationLoadingMore = false;
        } else {
          this.notificationLoading = false;
        }
      }
    },

    /**
     * 加载更多通知
     * @param pageSize 每页数量
     */
    async loadMoreNotifications(pageSize: number = 10) {
      if (
        !this.notificationPagination?.has_next ||
        this.notificationLoadingMore
      ) {
        return;
      }
      this.notificationLoadingMore = true;
      const nextPage = (this.notificationPagination?.current_page || 0) + 1;
      try {
        await this.getNotificationDetail(
          this.curNoticeTab,
          nextPage,
          pageSize,
          true
        );
      } finally {
        this.notificationLoadingMore = false;
      }
    },

    /**
     * 设置当前通知标签页
     * @param tab 标签页类型
     */
    setCurNoticeTab(tab: NoticeTab) {
      this.curNoticeTab = tab;
    },

    /**
     * 更新通知项
     * @param index 通知项索引
     * @param updatedNotification 更新后的通知对象
     */
    updateNotificationItem(index: number, updatedNotification: any) {
      if (this.notificationDetail[index]) {
        this.notificationDetail[index] = updatedNotification;
      }
    },

    /**
     * 获取是否有新增粉丝
     */
    async getHasNewFollower() {
      const response = await userApi.getFollowNotificationCount(GAME_ID);
      if (response.code == 200) {
        this.newFollowerCount = response.data.unread_count;
      } else {
        throw new Error(response.message);
      }
    },

    /**
     * 清除新增粉丝
     */
    clearNewFollower() {
      this.newFollowerCount = 0;
    },

    /**
     * 获取真实姓名修改记录
     */
    async getRealNameRecord() {
      const response = await userApi.getRealNameRecord(GAME_ID);
      if (response.code == 200) {
        this.realNameRecord = response.data;
      } else {
        throw new Error(response.message);
      }
    },
  },
});
